import { Button } from "@/components/flexkit/Button";
import { useUpdateWorkflow } from "@/queries/automation-queries";
import { useStore } from "jotai";
import {
    convertFormStepsToWorkflowStep,
    convertFormTriggersToProtobuf,
    type WorkflowEditFormStepData,
    type WorkflowEditFormTriggerData,
} from "@/components/automations/automation-edit-form-data";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { FC } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { DEFAULT_WORKFLOW_LIMITS, WorkflowDetailsTab, workflowDetailsUrl } from "@/utils/automations";
import { useNavigate } from "react-router-dom";
import type { PrimitiveAtom } from "jotai";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";

type SaveAutomationStepsButtonProps = {
    workflowId: string;
    editableStepsAtom: PrimitiveAtom<WorkflowEditFormStepData[] | null>;
    editableTriggersAtom: PrimitiveAtom<WorkflowEditFormTriggerData[]>;
    editableExecutorAtom: PrimitiveAtom<PlainMessage<Subject> | undefined>;
    editableMaxParallelAtom: PrimitiveAtom<number | undefined>;
    editableMaxTotalAtom: PrimitiveAtom<number | undefined>;
};

/**
 * This button saves all changes made to an automation, including steps, triggers, executor, and limits.
 */
export const SaveAutomationStepsButton: FC<SaveAutomationStepsButtonProps> = ({
    workflowId,
    editableStepsAtom,
    editableTriggersAtom,
    editableExecutorAtom,
    editableMaxParallelAtom,
    editableMaxTotalAtom,
}) => {
    const navigate = useNavigate();

    const { mutateAsync: updateWorkflow, isPending: isUpdatingWorkflow } = useUpdateWorkflow();
    const store = useStore();
    const { toast } = useToast();

    const save = async () => {
        const steps = store.get(editableStepsAtom);
        const triggers = store.get(editableTriggersAtom);
        const executor = store.get(editableExecutorAtom);
        const maxParallel = store.get(editableMaxParallelAtom);
        const maxTotal = store.get(editableMaxTotalAtom);

        if (!steps) {
            return;
        }

        try {
            await updateWorkflow({
                workflowId,
                executor,
                triggers: convertFormTriggersToProtobuf(triggers),
                action: new WorkflowAction({
                    limits: new WorkflowAction_Limits({
                        maxParallel: maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: convertFormStepsToWorkflowStep(steps),
                }),
            });
            navigate(workflowDetailsUrl({ id: workflowId, tab: WorkflowDetailsTab.Definition }));
        } catch (error) {
            toast({
                title: "Failed to save changes to the automation",
                description: formatError(error),
            });
        }
    };

    return (
        <Button
            variant="primary"
            data-tracking-id="save-automation-steps-automation-edit-page"
            loading={isUpdatingWorkflow}
            onClick={save}
        >
            Save
        </Button>
    );
};
